import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Trash2,
  Copy,
  Check,
  Loader2,
  Clock,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Mic,
  Camera,
  AlertTriangle,
} from 'lucide-react';
import { Button, Card } from '../components/ui';
import type { HistoryItem } from '@zolotalk/api-client';
import { accountApi, historyApi } from '../api/client';
import './HistoryPage.css';

const LANGUAGE_LABELS: Record<string, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  ru: 'Русский',
};

const languageLabel = (code: string) => LANGUAGE_LABELS[code] ?? code;

const TYPE_ICONS = {
  text: MessageSquare,
  voice: Mic,
  ocr: Camera,
} as const;

const TYPE_LABELS = {
  text: 'Text Translation',
  voice: 'Voice Session',
  ocr: 'OCR Capture',
} as const;

function DeleteConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  loading,
  itemType,
  deleteAll,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
  itemType: string;
  deleteAll: boolean;
}) {
  const modalRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const loadingRef = useRef(loading);

  useEffect(() => {
    onCloseRef.current = onClose;
    loadingRef.current = loading;
  }, [loading, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    modalRef.current?.querySelector<HTMLElement>('[data-modal-cancel]')?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loadingRef.current) {
        event.preventDefault();
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        modalRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled)') ?? []
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      trigger?.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        ref={modalRef}
        className="modal"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-modal-title"
      >
        <div className="modal-header">
          <h2 id="delete-modal-title" className="modal-title">
            {deleteAll ? 'Delete All Account Data' : 'Delete History Item'}
          </h2>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close delete confirmation"
            disabled={loading}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="modal-body">
          <p>
            {deleteAll ? (
              'Are you sure you want to delete all of your account data?'
            ) : (
              <>
                Are you sure you want to delete this <strong>{itemType}</strong> from your history?
              </>
            )}
          </p>
          <p className="modal-cascade">
            This also removes derived vocabulary, flashcards, drills, games, and associated media.
          </p>
          <p className="modal-warning">This action cannot be undone.</p>
        </div>
        <div className="modal-footer">
          <Button data-modal-cancel variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} loading={loading}>
            {loading ? <Loader2 size={16} /> : 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function HistoryPage() {
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [filterType, setFilterType] = useState<'all' | 'text' | 'voice' | 'ocr'>('all');
  const [deleteTarget, setDeleteTarget] = useState<HistoryItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [notice, setNotice] = useState<{
    kind: 'success' | 'pending' | 'error';
    text: string;
  } | null>(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await historyApi.getHistory({
        page,
        limit: 20,
        type: filterType === 'all' ? undefined : filterType,
      });
      setHistory(data.items);
      setTotalPages(data.totalPages);
      setTotal(data.total);
    } catch {
      setError('Failed to fetch history');
    } finally {
      setLoading(false);
    }
  }, [page, filterType]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleDelete = async () => {
    if (!deleteTarget && !deleteAllOpen) return;
    setDeleteLoading(true);
    setNotice(null);
    try {
      if (deleteAllOpen) {
        await accountApi.deleteAccount();
      } else {
        await historyApi.deleteHistoryItem(deleteTarget!.id);
      }

      if (deleteAllOpen) {
        setHistory([]);
        setTotal(0);
        setTotalPages(0);
      } else {
        setHistory(prev => prev.filter(item => item.id !== deleteTarget!.id));
        setTotal(prev => Math.max(0, prev - 1));
      }
      setNotice({
        kind: 'success',
        text: deleteAllOpen
          ? 'All account data was deleted.'
          : 'History item and its derived data were deleted.',
      });
      setDeleteTarget(null);
      setDeleteAllOpen(false);
    } catch {
      setNotice({
        kind: 'error',
        text: 'Deletion failed. Please retry.',
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCopy = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      setError('Failed to copy to clipboard');
    }
  };

  const getTypeIcon = (type: 'text' | 'voice' | 'ocr') => {
    const Icon = TYPE_ICONS[type];
    return <Icon size={16} aria-hidden="true" />;
  };

  const getTypeLabel = (type: 'text' | 'voice' | 'ocr') => TYPE_LABELS[type];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="page history-page">
      <div className="container">
        <header className="page-header">
          <h1>Translation History</h1>
          <p className="page-description">
            View and manage your past translations across text, voice, and camera modes.
          </p>
        </header>

        {error && (
          <div className="error-banner" role="alert">
            {error}
          </div>
        )}

        {notice && (
          <div
            className={`deletion-notice deletion-notice--${notice.kind}`}
            role={notice.kind === 'error' ? 'alert' : 'status'}
            aria-live="polite"
          >
            {notice.kind === 'success' ? (
              <Check size={18} aria-hidden="true" />
            ) : (
              <AlertTriangle size={18} aria-hidden="true" />
            )}
            <span>{notice.text}</span>
          </div>
        )}

        <div className="history-toolbar">
          <div className="filter-group">
            <label htmlFor="type-filter" className="visually-hidden">
              Filter by type
            </label>
            <select
              id="type-filter"
              className="filter-select"
              value={filterType}
              onChange={e => {
                setFilterType(e.target.value as typeof filterType);
                setPage(1);
              }}
            >
              <option value="all">All Types</option>
              <option value="text">Text Translation</option>
              <option value="voice">Voice Session</option>
              <option value="ocr">OCR Capture</option>
            </select>
          </div>
          <div className="results-count">
            {total > 0 ? (
              <>
                Showing {(page - 1) * 20 + 1}–{Math.min(page * 20, total)} of {total} results
              </>
            ) : (
              'No history items found'
            )}
          </div>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setDeleteAllOpen(true)}
            disabled={loading || total === 0}
          >
            <Trash2 size={16} aria-hidden="true" />
            <span>Delete all data</span>
          </Button>
        </div>

        {loading ? (
          <div className="history-loading" aria-live="polite">
            <Loader2 size={32} className="spinner" />
            <p>Loading history...</p>
          </div>
        ) : history.length === 0 ? (
          <div className="history-empty">
            <Clock size={48} className="empty-icon" aria-hidden="true" />
            <h2>No History Yet</h2>
            <p>Your translations will appear here once you start translating.</p>
          </div>
        ) : (
          <>
            <div className="history-list" role="list" aria-label="Translation history">
              {history.map(item => (
                <Card key={item.id} className="history-item" role="listitem">
                  <div className="history-item-header">
                    <div className="history-type-badge">
                      {getTypeIcon(item.type)}
                      <span>{getTypeLabel(item.type)}</span>
                    </div>
                    <div className="history-meta">
                      <time dateTime={item.createdAt} className="history-date">
                        <Clock size={14} aria-hidden="true" />
                        {formatDate(item.createdAt)}
                      </time>
                      <span
                        className="history-languages"
                        aria-label={`${languageLabel(item.sourceLanguage)} to ${languageLabel(item.targetLanguage)}`}
                      >
                        {languageLabel(item.sourceLanguage)} → {languageLabel(item.targetLanguage)}
                      </span>
                      <span className={`history-register register-${item.register}`}>
                        {item.register === 'auto'
                          ? 'Auto'
                          : item.register.charAt(0).toUpperCase() + item.register.slice(1)}
                      </span>
                    </div>
                  </div>

                  <div className="history-content">
                    <div className="history-source">
                      <span className="history-label">Source:</span>
                      <p className="history-text">{item.sourceText}</p>
                    </div>
                    <div className="history-translation">
                      <span className="history-label">Translation:</span>
                      <div className="translation-row">
                        <p className="history-text">{item.translatedText}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopy(item.translatedText, item.id)}
                          aria-label={copiedId === item.id ? 'Copied' : 'Copy translation'}
                          className="copy-button"
                        >
                          {copiedId === item.id ? <Check size={16} /> : <Copy size={16} />}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="history-actions">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setDeleteTarget(item)}
                      aria-label={`Delete ${getTypeLabel(item.type).toLowerCase()}`}
                    >
                      <Trash2 size={16} />
                      <span>Delete</span>
                    </Button>
                  </div>
                </Card>
              ))}
            </div>

            {totalPages > 1 && (
              <nav className="pagination" aria-label="History pagination">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(prev => Math.max(1, prev - 1))}
                  disabled={page === 1 || loading}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={16} />
                  <span>Previous</span>
                </Button>
                <span className="page-info" aria-live="polite">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={page === totalPages || loading}
                  aria-label="Next page"
                >
                  <span>Next</span>
                  <ChevronRight size={16} />
                </Button>
              </nav>
            )}
          </>
        )}

        <DeleteConfirmationModal
          isOpen={!!deleteTarget || deleteAllOpen}
          onClose={() => {
            setDeleteTarget(null);
            setDeleteAllOpen(false);
          }}
          onConfirm={handleDelete}
          loading={deleteLoading}
          itemType={deleteTarget ? getTypeLabel(deleteTarget.type) : ''}
          deleteAll={deleteAllOpen}
        />
      </div>
    </div>
  );
}
